<template>
  <a-modal
    :open="visible"
    title="EMR Tunnel 차단 IP 관리"
    :width="800"
    :footer="null"
    :zIndex="1050"
    :maskClosable="false"
    @cancel="handleCancel"
  >
    <div class="modal-body">
      <div class="hospital-info-section">
        <div class="info-item">
          <span class="label">병원명</span>
          <span class="value">{{ hospitalName }}</span>
        </div>
        <div class="info-item">
          <span class="label">사업자등록번호</span>
          <span class="value">{{ hospitalCNo }}</span>
        </div>
      </div>

      <a-divider />

      <div class="blocked-ip-section">
        <div class="section-header">
          <h4>차단된 IP 목록</h4>
          <a-button @click="fetchBlockedIps" :loading="loading" size="small">
            <template #icon><ReloadOutlined /></template>
            새로고침
          </a-button>
        </div>

        <a-table
          :columns="columns"
          :data-source="blockedIps"
          :loading="loading"
          :pagination="pagination"
          size="small"
          row-key="ip"
          @change="handleTableChange"
        >
          <template #emptyText>
            <div class="empty-text">차단된 IP가 없습니다.</div>
          </template>
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'blockedAt'">
              {{ formatDateTime(record.blockedAt) }}
            </template>
            <template v-else-if="column.key === 'action'">
              <a-popconfirm
                :title="`IP ${record.ip}의 차단을 해제하시겠습니까?`"
                ok-text="해제"
                cancel-text="취소"
                @confirm="handleUnblock(record.ip)"
              >
                <a-button
                  type="primary"
                  danger
                  size="small"
                  :loading="unblockingIp === record.ip"
                >
                  차단 해제
                </a-button>
              </a-popconfirm>
            </template>
          </template>
        </a-table>
      </div>

      <div class="button-wrap">
        <a-button @click="handleCancel">닫기</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from "ant-design-vue";
import { ReloadOutlined } from "@ant-design/icons-vue";

export default {
  name: "EmrBlockedIpModal",
  components: {
    ReloadOutlined,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    hospitalName: {
      type: String,
      default: "",
    },
    hospitalCNo: {
      type: String,
      default: "",
    },
    companyId: {
      type: String,
      default: "",
    },
  },
  emits: ["update:visible"],
  data() {
    return {
      loading: false,
      blockedIps: [],
      unblockingIp: null,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
        showTotal: (total) => `총 ${total}개`,
      },
      columns: [
        {
          title: "IP 주소",
          dataIndex: "ip",
          key: "ip",
          align: "center",
          width: 150,
        },
        {
          title: "차단 사유",
          dataIndex: "reason",
          key: "reason",
          align: "center",
        },
        {
          title: "시도 횟수",
          dataIndex: "attemptCount",
          key: "attemptCount",
          align: "center",
          width: 100,
        },
        {
          title: "마지막 HW ID",
          dataIndex: "lastHwId",
          key: "lastHwId",
          align: "center",
          width: 150,
          customRender: ({ text }) => text || "-",
        },
        {
          title: "차단 일시",
          key: "blockedAt",
          align: "center",
          width: 160,
        },
        {
          title: "관리",
          key: "action",
          align: "center",
          width: 100,
        },
      ],
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetAndFetch();
      }
    },
  },
  methods: {
    resetAndFetch() {
      this.pagination.current = 1;
      this.blockedIps = [];
      this.fetchBlockedIps();
    },
    async fetchBlockedIps() {
      this.loading = true;
      try {
        const response = await this.$apiInstance.get(
          `/companies/emr-tunnel/${this.companyId}/blocked-ips`,
          {
            params: {
              page: this.pagination.current,
              limit: this.pagination.pageSize,
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          this.blockedIps = response.data.items;
          this.pagination.total = response.data.total;
        }
      } catch (error) {
        console.error("차단 IP 목록 조회 실패:", error);
        if (error.response?.status === 403) {
          message.error("권한이 없습니다.");
        } else {
          message.error("차단 IP 목록을 불러오는데 실패했습니다.");
        }
      } finally {
        this.loading = false;
      }
    },
    async handleUnblock(ip) {
      this.unblockingIp = ip;
      try {
        const encodedIp = encodeURIComponent(ip);
        const response = await this.$apiInstance.delete(
          `/companies/emr-tunnel/${this.companyId}/blocked-ips/${encodedIp}`
        );

        if (response.status === 200 || response.status === 201) {
          message.success(`IP ${ip}의 차단이 해제되었습니다.`);
          await this.fetchBlockedIps();
        }
      } catch (error) {
        console.error("IP 차단 해제 실패:", error);
        if (error.response?.status === 404) {
          message.error("해당 IP가 차단 목록에 없습니다.");
        } else if (error.response?.status === 403) {
          message.error("권한이 없습니다.");
        } else {
          message.error("IP 차단 해제에 실패했습니다.");
        }
      } finally {
        this.unblockingIp = null;
      }
    },
    handleTableChange(paginationInfo) {
      this.pagination.current = paginationInfo.current;
      this.pagination.pageSize = paginationInfo.pageSize;
      this.fetchBlockedIps();
    },
    handleCancel() {
      this.$emit("update:visible", false);
    },
    formatDateTime(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
  },
};
</script>

<style scoped>
.modal-body {
  padding: 8px 0;
}

.hospital-info-section {
  display: flex;
  gap: 32px;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.info-item {
  display: flex;
  gap: 8px;
}

.info-item .label {
  font-weight: 500;
  color: #666;
}

.info-item .value {
  font-weight: 600;
  color: #333;
}

.blocked-ip-section {
  margin-top: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.empty-text {
  padding: 24px;
  color: #999;
}

.button-wrap {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
</style>
